import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { createDocEntryStore } from './docStore';
import { readState, writeState } from './docState';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';
import { loadPdf, renderPdfPageBand } from './pdfRender';
import { numberBoxes, shrinkDataUrl } from './partCrop';
import { LISTING_CROPS_MODEL, forPrompt, listingSchemas, tagIndex, type CropNote } from './listingCropsRun';
import type { PartItem } from './partsRun';
import { useAssemblySteps, type StepBand } from './categoryBands';
import { cutCropFrom } from './cutCrop';
import { haltGate } from './runHalt';

// WHERE EVERY PART IS DRAWN IN THE MANUAL'S STEPS — one search, in Phase 2's listing-crops row.
//
// ONE SEARCH WHERE THERE WERE TWO. The hardware was looked for here, and every part again by a
// second search in Phase 3 ("In the steps") which asked each step about the parts the
// repartition gave it. Two pipelines boxing the same screws on the same pages, into two stores.
// This one is kept because it needs no repartition — so it runs in Phase 2 — and Phase 3's
// readers (tool sizing, Phase 5's step pictures) read its answers through schemaCropsRun.
//
// EVERY PART, while the photograph call asks only about the components. A listing picture is of
// an ASSEMBLED product: the screws are driven, the dowels are in the joints and the tools are in
// a drawer, so the parts it can find are the components. The manual draws all of them — in the
// parts list, and again in every step that fits them — so a component is looked for in both,
// and hardware and tools in the manual alone.
//
// TWO CALLS PER ASSEMBLY STEP: what is drawn, then which part each one is.
//
// ONE CALL PER PART did not work, and neither did one call for all of them. Asked "where is the
// barrel nut in this step", a model answers with a rectangle — that is the question it is worst
// at refusing, and a step drawing holds a dozen small hatched shapes of which one always
// resembles a barrel nut enough to be boxed. Nine such calls are nine forced answers, each
// blind to the other eight: four parts claiming the same pixels, one part claimed twice, and a
// step that uses none of them still answering.
//
// So the FIND call is given no part to look for. It boxes what is drawn — every separate piece
// in the picture — which is a question about the picture and is answered by looking at it.
// Nothing can be forced out of it, because nothing was asked for.
//
// The MATCH call then works over a closed set: every candidate at once, each taking at most one
// part, and the parts that are not in this step have no rectangle to be given. And it is shown
// the rectangles DRAWN ON THE PICTURE, numbered — four coordinates in a paragraph would be
// arithmetic on an image, which is the one thing a vision model is not for. It also says, for a
// tool, which of the other rectangles it is working on (`touches`) — what tool sizing measures
// the tool against.
//
// The letters carry most of it. A step labels its parts with a letter in a circle and a line to
// the thing it names, so the find call is asked which letter points at each box, and the match
// call is told to trust that over resemblance. See the backend, above _STEP_FIND_HEAD.
//
// THE ANSWERS LAND IN THE SAME ROW, and they are the same shape of answer — a picture, and a
// rectangle on it. What differs is which picture: a listing crop names a URL, and one of these
// names ONE OF A STEP'S BANDS, which is rendered from the PDF where the step is drawn.
//
// THE BAND AND NOT THE PAGE IT WAS CUT FROM. The box was measured against the band — that is
// the picture the model was shown — and putting it back on the whole page meant the row drew
// something nobody was ever asked about: a page with two steps on it, a box from one of them,
// and no way to see which step the answer is about. The pictures in this row are now exactly
// the pictures the call carries.
export type HardwareCrop = {
  // The step it was found in, by category — the id every other per-step answer in this app is
  // keyed by — and WHICH OF THAT STEP'S PICTURES, 0-based, a step spanning two pages having
  // two. The rectangle is 0..1 within that picture, the same normalised space a listing crop's
  // bbox is in.
  step: string;
  shot: number;
  bbox: [number, number, number, number];
  // The same rectangle before the server's padding, as the model sent it. Kept for the reason
  // ListingCrop keeps it: the padding clamps at the page's edges and cannot be taken back off
  // by arithmetic.
  rawBbox?: [number, number, number, number];
  // WHAT THIS DRAWING SHOWS THE TOOL WORKING ON — part indices, and only on a tool's crop.
  // Empty means the model looked and said "drawn alone"; absent means nobody asked.
  touches?: number[];
};

// By part INDEX, like every other per-part answer in this phase.
export type ByHardwarePart = Record<number, HardwareCrop[]>;

// WHAT THE FIND CALL SAW, before anybody said what it is. Numbered within its step — `n` is
// what is painted on the picture the match call reads, so the two calls and the panel all mean
// the same rectangle by the same number.
export type Candidate = {
  step: string;
  shot: number;
  n: number;
  bbox: [number, number, number, number];
  rawBbox?: [number, number, number, number];
  // The callout letter whose line ends on it, where the step draws one.
  letter?: string;
  // Which part the match call said it is, once that has been asked. `null` is an answer — this
  // rectangle is none of the parts — and undefined is the absence of one.
  part?: number | null;
};

// By STEP, keyed by category, in the order they were found.
export type ByStepCandidates = Record<string, Candidate[]>;

type Snapshot = {
  busy: boolean;
  byPart: ByHardwarePart;
  // Every rectangle the find call drew, kept whether or not a part was found in it: a candidate
  // nobody could name is the most interesting thing this run has to say.
  candidates: ByStepCandidates;
  // ONE NOTE PER CALL, and a call is a part in a step: see askKey. What the model thought and
  // what it replied, which is the only way to read a column that came back empty.
  notes: Record<string, CropNote>;
  // How many calls were made, so "nothing found" can be told from "never run".
  looked: number;
  // WHICH CALLS the run in flight has finished with, by that same key. Not persisted: it is a
  // fact about calls in the air, and the row draws what has answered while the rest wait.
  read: string[];
  // WHICH PARTS HAVE BEEN THROUGH A RUN, by index — the other half of the same distinction,
  // and what a cell needs to tell "found nothing" from "never asked".
  asked: number[];
  error: string | null;
};

// NEW KEYS, because the answers changed shape: a crop named a page and now names a step and one
// of its pictures. An old answer read under the new name would be a box with no picture to put
// it on, which is worse than the run it costs to ask again.
const cropsKey = (docId: string) => `notice.stepCrops.${docId}`;
const notesKey = (docId: string) => `notice.stepCropsNotes.${docId}`;
const lookedKey = (docId: string) => `notice.stepCropsLooked.${docId}`;
const askedKey = (docId: string) => `notice.stepCropsAsked.${docId}`;

const foundKey = (docId: string) => `notice.stepCands.${docId}`;

// ONE CALL, NAMED: this stage, in this step. The key the notes and the in-flight list are kept
// under, and the only place the two halves are joined into one string.
export type Stage = 'find' | 'match';
export const STAGES: Stage[] = ['find', 'match'];
export const askKey = (category: string, stage: Stage) => `${category}#${stage}`;

const store = createDocEntryStore<Snapshot>((docId) => ({
  snapshot: {
    busy: false,
    byPart: readState<ByHardwarePart>(docId, cropsKey(docId), {}),
    candidates: readState<ByStepCandidates>(docId, foundKey(docId), {}),
    notes: readState<Record<string, CropNote>>(docId, notesKey(docId), {}),
    looked: readState<number>(docId, lookedKey(docId), 0),
    read: [],
    asked: readState<number[]>(docId, askedKey(docId), []),
    error: null,
  },
}));

export const useHardwareCrops = store.useSnapshot;

// WHICH PARTS THIS CALL IS FOR: all of them, supplied or not. A tool the carton does not hold
// is still drawn in the steps that use it, and those drawings are what size it (toolSizing).
export const pageCropAsks = (parts: PartItem[]): number[] => parts.map((_, i) => i);

// ONE STEP'S PICTURES: its diagram bands, in printed order, each knowing the page and the strip
// of it it was cut from.
export type StepShot = { page: number; top: number; bottom: number; src: string };

// EVERY STEP'S, in one pass over the PDF. The document is opened once for the whole run rather
// than once per step — it is the same file, and pdf.js parses it afresh every time it is asked.
export async function stepShots(
  file: Blob, steps: StepBand[],
): Promise<Map<string, StepShot[]>> {
  const out = new Map<string, StepShot[]>();
  let task: PDFDocumentLoadingTask | null = null;
  try {
    task = loadPdf(await file.arrayBuffer());
    const doc: PDFDocumentProxy = await task.promise;
    for (const step of steps) {
      const shots: StepShot[] = [];
      for (const page of step.pages) {
        const band = step.bands[page] ?? { top: 0, bottom: 1 };
        const src = await renderPdfPageBand(doc, page, band.top, band.bottom);
        if (src) shots.push({ page, top: band.top, bottom: band.bottom, src });
      }
      if (shots.length) out.set(step.category, shots);
    }
  } catch {
    // A step whose bands will not render is a step with no pictures: the run skips it, which
    // is what an unreadable page honestly looks like.
  } finally {
    void task?.destroy();
  }
  return out;
}

// EVERY STEP'S BANDS, FOR A COMPONENT THAT IS DRAWING THEM — the run renders its own, and this
// is the same pass done where the pictures are shown rather than sent. Empty until the steps
// are worth rendering: a manual nobody has run this on has nothing to draw.
export function useStepShots(
  file: Blob | null, steps: StepBand[],
): Map<string, StepShot[]> {
  // Keyed by the CATEGORIES rather than by the array, which is rebuilt every render and would
  // restart a pass over the PDF on each one.
  const key = steps.map((one) => one.category).join('|');
  const [shots, setShots] = useState<Map<string, StepShot[]>>(EMPTY_SHOTS);
  useEffect(() => {
    if (!file || !steps.length) { setShots(EMPTY_SHOTS); return undefined; }
    let gone = false;
    void stepShots(file, steps).then((made) => { if (!gone) setShots(made); });
    return () => { gone = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, key]);
  return shots;
}

// One empty map, so a component with no steps does not get a new one on every render.
const EMPTY_SHOTS: Map<string, StepShot[]> = new Map();

// THE PICTURE THE MATCH CALL READS: one band of the step, with its candidates drawn on it and
// numbered. Built in the browser because the browser is what has the pixels — the server was
// sent a shrunk copy and keeps nothing.
//
// THE RAW RECTANGLE where there is one: `bbox` carries the padding the server adds for cutting
// a crop, and what this picture is for is showing the model exactly what was boxed.
const annotate = (band: StepShot, found: Candidate[]) =>
  numberBoxes(band.src, found.map((c) => ({ n: c.n, bbox: c.rawBbox ?? c.bbox })));

// THE QUESTION THIS BUTTON WOULD SEND, for the badge beside it — built by the server, from the
// very same blocks the run posts.
//
// EITHER STAGE, because there are two calls per step now and the panel walks them. The match
// stage needs the candidates, so before a run there is nothing to preview but the words: that
// is honest, and it is why the two stages are read step by step rather than as one question.
export async function stepHardwarePrompt(
  stage: Stage, parts: PartItem[], asks: number[], drawings: (string | null)[],
  shots: StepShot[], found: Candidate[],
): Promise<{ prompt: string; urls: string[]; model: string }> {
  if (stage === 'find') {
    const { data } = await axios.post<{ prompt?: string }>('/api/notice/step-hardware', {
      preview: true,
      stage,
      images: shots.map((one) => one.src),
      // The parts go up even here: whether the manual letters anything at all is read off
      // them, and a step with no callouts must not be told to look for some.
      parts: asks.map((i) => forPrompt(parts[i], i)),
    });
    return {
      prompt: data.prompt || '',
      urls: shots.map((one) => one.src),
      model: LISTING_CROPS_MODEL,
    };
  }
  // ONE MATCH CALL PER BAND, because the numbered picture is one picture. The numbers stay the
  // step's own, so a step of two bands is two calls that cannot renumber each other.
  const band = shots.find((_, i) => found.some((c) => c.shot === i));
  const mine = band ? found.filter((c) => c.shot === shots.indexOf(band)) : [];
  const drawn = band ? await annotate(band, mine) : null;
  const { data } = await axios.post<{ prompt?: string }>('/api/notice/step-hardware', {
    preview: true,
    stage,
    images: drawn ? [drawn] : [],
    parts: asks.map((i) => forPrompt(parts[i], i)),
    schemas: asks.filter((i) => drawings[i]).map((i) => ({ tag: String(i) })),
    found: mine.map((c) => ({ n: c.n, ...(c.letter ? { letter: c.letter } : {}) })),
  });
  return {
    prompt: data.prompt || '',
    urls: [
      ...(drawn ? [drawn] : []),
      ...asks.map((i) => drawings[i]).filter((d): d is string => !!d),
    ],
    model: LISTING_CROPS_MODEL,
  };
}

// How many STEPS are in the air at once. A step is two calls now, run one after the other —
// the second reads the first's answer — so this is the only place there is any width.
const AT_ONCE = 3;

// EVERY STEP, BOTH CALLS. There is no scoping to a part: the find call has no part in it, and
// the match call is one question about the whole rectangle set.
export async function runPageCrops(
  docId: string, file: Blob, steps: StepBand[], parts: PartItem[],
  drawings: (string | null)[] = [],
): Promise<void> {
  const asks = pageCropAsks(parts);
  if (!asks.length || !steps.length) return;

  store.patch(docId, {
    busy: true, error: null, looked: 0, read: [], byPart: {}, candidates: {}, notes: {},
  });
  // STOP SENDS NO NEXT CALL (see runHalt): the ones out land and are kept.
  const halted = haltGate(docId);

  // EVERY STEP'S BANDS, rendered before the first call goes out. One pass over the PDF rather
  // than one per step: the document is opened once, and a step that spans two pages takes two
  // of these into the same find call.
  const shots = await stepShots(file, steps);
  const reading = steps.filter((step) => shots.get(step.category)?.length);
  if (!reading.length) {
    store.patch(docId, { busy: false, error: 'failed' });
    return;
  }

  const described = asks.map((i) => forPrompt(parts[i], i));
  const schemas = await listingSchemas(asks, drawings);
  const byPart: ByHardwarePart = {};
  const candidates: ByStepCandidates = {};
  const notes: Record<string, CropNote> = {};
  const read: string[] = [];
  let looked = 0;
  let failed = 0;

  const landed = () => store.patch(docId, {
    byPart: { ...byPart },
    candidates: { ...candidates },
    notes: { ...notes },
    looked,
    read: [...read],
  });

  const said = (key: string, data: { reasoning?: string; raw?: string; unreadable?: boolean }) => {
    notes[key] = {
      reasoning: data.reasoning || '',
      raw: data.raw || '',
      ...(data.unreadable ? { unreadable: true } : {}),
    };
    if (data.unreadable) failed += 1;
  };

  // WHAT IS DRAWN. Every band of the step in one call, and no part named in it.
  const find = async (step: StepBand, bands: StepShot[]): Promise<Candidate[]> => {
    const key = askKey(step.category, 'find');
    try {
      const { data } = await axios.post<{
        found: {
          bbox: [number, number, number, number];
          raw_bbox?: [number, number, number, number];
          letter?: string;
          image?: number;
        }[];
        reasoning?: string; raw?: string; unreadable?: boolean;
      }>('/api/notice/step-hardware', {
        model: LISTING_CROPS_MODEL,
        stage: 'find',
        images: await Promise.all(bands.map((b) => shrinkDataUrl(b.src, 1400))),
        parts: described,
      });
      said(key, data);
      return (data.found || []).map((one, i) => ({
        step: step.category,
        shot: Math.min(bands.length, Math.max(1, one.image ?? 1)) - 1,
        n: i + 1,
        bbox: one.bbox,
        ...(one.raw_bbox ? { rawBbox: one.raw_bbox } : {}),
        ...(one.letter ? { letter: one.letter } : {}),
      }));
    } catch (e) {
      console.warn('[step-hardware] find failed', step.num, e);
      notes[key] = { reasoning: '', raw: `The call failed: ${e}` };
      failed += 1;
      return [];
    }
  };

  // WHICH PART EACH ONE IS. One call per band that holds candidates, the numbers being the
  // step's own throughout.
  const match = async (step: StepBand, bands: StepShot[], found: Candidate[]) => {
    const key = askKey(step.category, 'match');
    const by = new Map(found.map((c) => [c.n, c]));
    for (let shot = 0; shot < bands.length; shot += 1) {
      const mine = found.filter((c) => c.shot === shot);
      if (!mine.length) continue;
      const drawn = await annotate(bands[shot], mine);
      if (!drawn) continue;
      try {
        const { data } = await axios.post<{
          matched: { n: number; tag: string | null; touches?: number[] }[];
          reasoning?: string; raw?: string; unreadable?: boolean;
        }>('/api/notice/step-hardware', {
          model: LISTING_CROPS_MODEL,
          stage: 'match',
          images: [await shrinkDataUrl(drawn, 1400)],
          parts: described,
          schemas,
          found: mine.map((c) => ({ n: c.n, ...(c.letter ? { letter: c.letter } : {}) })),
        });
        // A step of two bands is two match calls and one note: the second overwrites the
        // first's reasoning, which is the price of one panel per stage. The boxes are the
        // answer; the note is the commentary.
        said(key, data);
        const matched = data.matched || [];
        for (const one of matched) {
          const found_at = by.get(one.n);
          if (!found_at) continue;
          const part = one.tag ? tagIndex(one.tag, parts.length) : null;
          found_at.part = part != null && asks.includes(part) ? part : null;
        }
        for (const one of matched) {
          const found_at = by.get(one.n);
          if (found_at?.part == null) continue;
          // THE RECTANGLES A TOOL IS ON, turned into the parts they were matched to — once
          // every rectangle of this band has been named, which is why this is a second pass.
          // Never the tool itself, and never a rectangle nobody could name.
          const touches = parts[found_at.part]?.tool && Array.isArray(one.touches)
            ? [...new Set(one.touches
              .map((n) => by.get(n)?.part)
              .filter((p): p is number => p != null && p !== found_at.part))]
            : undefined;
          (byPart[found_at.part] ||= []).push({
            step: step.category,
            shot: found_at.shot,
            bbox: found_at.bbox,
            ...(found_at.rawBbox ? { rawBbox: found_at.rawBbox } : {}),
            ...(touches ? { touches } : {}),
          });
        }
      } catch (e) {
        console.warn('[step-hardware] match failed', step.num, e);
        notes[key] = { reasoning: '', raw: `The call failed: ${e}` };
        failed += 1;
      }
    }
  };

  const look = async (step: StepBand) => {
    const bands = shots.get(step.category) ?? [];
    const found = await find(step, bands);
    candidates[step.category] = found;
    looked += 1;
    read.push(askKey(step.category, 'find'));
    landed();
    if (found.length && !halted()) await match(step, bands, found);
    looked += 1;
    read.push(askKey(step.category, 'match'));
    landed();
  };

  const queue = [...reading];
  await Promise.all(Array.from({ length: Math.min(AT_ONCE, queue.length) }, async () => {
    for (let step = queue.shift(); step && !halted(); step = queue.shift()) await look(step);
  }));

  // ASKED, only if something was actually read — a run whose every call failed has looked at
  // nothing, and marking its parts answered would turn an outage into "this part is drawn
  // nowhere in the manual", which is the one reading nobody can argue with.
  // A STOPPED RUN ASKED NOTHING: it looked at some steps, and calling the rest answered would
  // say their parts are drawn nowhere.
  const done = failed < looked && !halted();
  const asked = done ? asks : store.get(docId).asked;

  writeState(docId, cropsKey(docId), byPart);
  writeState(docId, foundKey(docId), candidates);
  writeState(docId, notesKey(docId), notes);
  writeState(docId, lookedKey(docId), looked);
  writeState(docId, askedKey(docId), asked);
  store.patch(docId, { busy: false, read: [], asked, error: done ? null : 'failed' });
}

// ONE PART'S PAGE CROPS, CUT OUT — the pictures a piece of hardware's 3D model can be built
// from beside its parts-list drawing. Each one is where the manual draws the part IN USE: the
// screw through its rail, the washer under its head, which a drawing of the part alone does
// not say.
//
// CUT HERE, to a small JPEG, because the page they are cut from exists only in this browser
// (rendered from the PDF) and the server keeps none of it. The same bytes are what the tile
// shows and what a run is sent, so the row on screen is exactly what the model is given.
//
// `key` names the crop by its step, picture and box, so a choice of crops survives a
// re-render and dies with the crop when a re-run moves it.
export type StepCropPicture = { key: string; src: string };

export function usePartStepCrops(
  docId: string, file: Blob | null, partAt: number | null | undefined,
): StepCropPicture[] {
  const { byPart } = useHardwareCrops(docId);
  const { steps } = useAssemblySteps(docId);
  const mine = partAt == null ? NO_CROPS : byPart[partAt] ?? NO_CROPS;
  const said = mine.map((c) => `${c.step}#${c.shot}#${c.bbox.join(',')}`).join('|');
  const wanted = useMemo(
    () => steps.filter((s) => mine.some((c) => c.step === s.category)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [steps, said],
  );
  const shots = useStepShots(file, wanted);
  const [cut, setCut] = useState<StepCropPicture[]>([]);
  useEffect(() => {
    let alive = true;
    void Promise.all(mine.map(async (c) => {
      const page = shots.get(c.step)?.[c.shot]?.src;
      const src = page ? await cutCropFrom(page, c.bbox) : null;
      return src ? [{ key: `${c.step}#${c.shot}#${c.bbox.join(',')}`, src }] : [];
    })).then((all) => { if (alive) setCut(all.flat()); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shots, said]);
  return cut;
}

const NO_CROPS: HardwareCrop[] = [];
